/**
 * Typed client for the Ferma Hub API.
 *
 * Two rules encoded here, both paid for in the original Device Lab:
 *
 * 1. **The token is short-lived and per-person** (see `src/auth`), never a permanent API key. In
 *    production the UI and the API share one origin (nginx serves this build at `/`, proxies `/v1`),
 *    so `baseUrl` is empty and the token never travels to another host.
 * 2. **WebSocket endpoints take the token as a query parameter**, because browsers cannot set
 *    headers on a WebSocket handshake. Every `-ws` route on the server expects `?token=`.
 */

import type {
  Dashboard,
  PostHistory,
  Direction,
  DirectionsList,
  MarketingAccount,
  ProfilePassState,
  ProfileSnapshot,
  Publication,
  TargetLink,
  UnlistedLink,
  CardChoice,
  Device,
  DistributeStarted,
  DistributeTarget,
  Distribution,
  Hub,
  Job,
  LogPage,
  LoginOptions,
  LoginResponse,
  RangeAccepted,
  Scenario,
  ScenarioStep,
  ScenariosList,
  SessionStart,
  Post,
  PostCheck,
  PostEdit,
  SourceCreated,
  SourceKind,
  SourcesList,
  User,
  Video,
  VideoCreated, MyKey, ServiceKey, OAuthRequest, UserRow, UsersList } from './types'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: unknown,
    message?: string,
  ) {
    super(message ?? reasonIn(detail) ?? `HTTP ${status}`)
    this.name = 'ApiError'
  }
}

/** The sentence the service sent, when it sent one: `{detail: {hint}}`, `{detail: {error}}`, `{detail: "…"}`. */
export function reasonIn(detail: unknown): string | null {
  if (!detail || typeof detail !== 'object') return null
  const inner = (detail as { detail?: unknown }).detail ?? detail
  if (typeof inner === 'string') return inner
  if (inner && typeof inner === 'object') {
    const d = inner as { hint?: unknown; error?: unknown }
    if (typeof d.hint === 'string') return d.hint
    if (typeof d.error === 'string') return d.error
  }
  return null
}

/** Thrown for 409 on session:start — the phone is held and nobody can take it from the holder
 *  (2026-10-08: no takeover for anybody). `kind` says by whom: a colleague (`device_busy`, frees itself
 *  when they stop or go idle) or automatic posting (`device_posting`, held until the posting ends;
 *  `posting` is the distribution id or `try:<job>`). */
export class DeviceBusyError extends ApiError {
  constructor(
    readonly kind: 'device_busy' | 'device_posting',
    readonly heldForS: number | null,
    readonly heldBy: string | null,
    readonly posting: string | null,
    detail: unknown,
  ) {
    super(409, detail, kind === 'device_posting' ? 'device posting' : 'device busy')
    this.name = 'DeviceBusyError'
  }
}

export interface TokenSource {
  /** Current bearer token; may be refreshed behind our back by the auth layer. */
  token(): string
}

export class TikTokHubApi {
  constructor(
    readonly baseUrl: string,
    private readonly auth: TokenSource,
  ) {
    this.baseUrl = baseUrl.replace(/\/+$/, '')
  }

  /** Raw response — for streaming endpoints (`/video`) and uploads where we read/write the body ourselves. */
  async raw(path: string, init: RequestInit = {}): Promise<Response> {
    return fetch(this.baseUrl + path, {
      ...init,
      headers: { Authorization: `Bearer ${this.auth.token()}`, ...(init.headers ?? {}) },
    })
  }

  /**
   * Called when the server rejects our token (401). Set by the root so one message can replace what
   * used to happen: every panel failing on its own schedule while the person guessed why.
   */
  onUnauthorized?: () => void

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const res = await this.raw(path, init)
    if (res.status === 401) this.onUnauthorized?.()
    if (!res.ok) throw new ApiError(res.status, await safeJson(res))
    if (res.status === 204) return undefined as T
    return (await res.json()) as T
  }

  async json<T>(path: string, body: unknown, method = 'POST'): Promise<T> {
    return this.request<T>(path, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  }

  /** ws(s):// URL with the token in the query string (browsers cannot send handshake headers). */
  wsUrl(path: string): string {
    const origin = this.baseUrl || window.location.origin
    return (
      origin.replace(/^http/, 'ws') +
      path +
      (path.includes('?') ? '&' : '?') +
      `token=${encodeURIComponent(this.auth.token())}`
    )
  }

  /** Absolute URL for an artifact path returned by the API (`/v1/artifacts/...`). */
  artifactUrl(path: string): string {
    return path.startsWith('http') ? path : this.baseUrl + path
  }

  // ---- sign-in (no token yet, so not through `raw`) ----

  async loginOptions(): Promise<LoginOptions> {
    const res = await fetch(`${this.baseUrl}/v1/ui/login-options`)
    if (!res.ok) throw new ApiError(res.status, await safeJson(res))
    return (await res.json()) as LoginOptions
  }

  login(email: string, password: string): Promise<LoginResponse> {
    return this.signIn('/v1/ui/login', { email, password })
  }

  /** `credential` is the ID token Google Identity Services handed the page. */
  loginWithGoogle(credential: string): Promise<LoginResponse> {
    return this.signIn('/v1/ui/google', { credential })
  }

  private async signIn(path: string, body: object): Promise<LoginResponse> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new ApiError(res.status, await safeJson(res))
    return (await res.json()) as LoginResponse
  }

  me() {
    return this.request<{ kind: 'user' | 'service'; user?: User }>('/v1/ui/me')
  }

  /** Extend this person's session; the server keeps identity and role and refuses past the ceiling. */
  refreshSession() {
    return this.json<LoginResponse & { max_age_left_s?: number }>('/v1/ui/refresh', {})
  }

  // ---- marketing: directions, phone bindings, accounts (plan Stage 1) ----

  directions() {
    return this.request<DirectionsList>('/v1/directions')
  }

  addDirection(body: { direction_id: string; name: string; color: string }) {
    return this.json<Direction>('/v1/directions', body)
  }

  editDirection(id: string, body: { name?: string; color?: string }) {
    return this.json<Direction>(`/v1/directions/${encodeURIComponent(id)}`, body, 'PATCH')
  }

  /** For ever — the service refuses another direction later (409 `direction_is_fixed`). */
  bindDirection(deviceId: string, directionId: string) {
    return this.json<{ direction_id: string }>(`/v1/devices/${encodeURIComponent(deviceId)}/direction`,
      { direction_id: directionId, confirm: true }, 'PUT')
  }

  accounts(filter: Partial<Record<'direction' | 'state' | 'serial' | 'platform', string>> = {}) {
    const q = new URLSearchParams(Object.entries(filter).filter(([, v]) => v) as [string, string][])
    return this.request<{ accounts: MarketingAccount[] }>(`/v1/accounts${q.toString() ? `?${q}` : ''}`)
  }

  addAccount(body: { email: string; handle?: string; device_id: string; platform?: string; state?: string; note?: string }) {
    return this.json<MarketingAccount>('/v1/accounts', body)
  }

  editAccount(id: string, body: { state?: string; reason?: string; note?: string; email?: string; handle?: string;
                                  site?: string | null; confirm?: boolean }) {
    return this.json<MarketingAccount>(`/v1/accounts/${encodeURIComponent(id)}`, body, 'PATCH')
  }

  // ---- the profile pass: phones read their own profiles (plan Stage 3) ----

  profilePass() {
    return this.request<ProfilePassState>('/v1/profile-pass')
  }

  /** Admin only: switch the schedule on or off. */
  setProfilePass(auto: boolean) {
    return this.json<ProfilePassState>('/v1/profile-pass', { auto }, 'PUT')
  }

  /** Admin only: read this account's profile now, on its phone (202; the result lands in `accountProfile`). */
  readProfile(accountId: string) {
    return this.json<{ started: boolean }>(`/v1/accounts/${encodeURIComponent(accountId)}/profile:read`, {})
  }

  accountProfile(accountId: string, limit = 5) {
    return this.request<{ snapshots: ProfileSnapshot[]; running: boolean }>(
      `/v1/accounts/${encodeURIComponent(accountId)}/profile?limit=${limit}`)
  }

  // ---- the dashboard (plan Stage 4) ----

  dashboard(filter: { days?: number; direction?: string; serial?: string; account_id?: string; since?: string; until?: string } = {}) {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(filter)) if (v) q.set(k, String(v))
    return this.request<Dashboard>(`/v1/dashboard${q.toString() ? `?${q}` : ''}`)
  }

  publicationHistory(id: string) {
    return this.request<PostHistory>(`/v1/publications/${encodeURIComponent(id)}/history`)
  }

  // ---- publications: every post on every phone (plan Stage 2) ----

  publications(filter: Partial<Record<'direction' | 'serial' | 'account_id' | 'since' | 'until' | 'video_id', string>> & {
    no_account?: boolean; no_url?: boolean; limit?: number
    /** only the posts a profile reading saw on the phone — what people are shown */
    on_phone?: boolean
  } = {}) {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(filter)) if (v) q.set(k, String(v))
    return this.request<{ publications: Publication[]; total: number }>(`/v1/publications${q.toString() ? `?${q}` : ''}`)
  }

  editPublication(id: string, body: { url?: string | null; account_id?: string | null; link_id?: string | null; note?: string }) {
    return this.json<Publication>(`/v1/publications/${encodeURIComponent(id)}`, body, 'PATCH')
  }

  // ---- target links (plan 2.3) ----

  links() {
    return this.request<{ links: TargetLink[]; unlisted: UnlistedLink[]; shared_warn_phones: number }>('/v1/links')
  }

  addLink(body: { url: string; name?: string; direction_id?: string | null; note?: string }) {
    return this.json<TargetLink>('/v1/links', body)
  }

  editLink(id: string, body: { name?: string; direction_id?: string | null; note?: string; retired?: boolean }) {
    return this.json<TargetLink>(`/v1/links/${encodeURIComponent(id)}`, body, 'PATCH')
  }

  // ---- users (admin-only on the service: 403 `insufficient_role` for a tester) ----

  users() {
    return this.request<UsersList>('/v1/users')
  }

  createUser(body: { email: string; password: string; role?: string; name?: string }) {
    return this.json<UserRow>('/v1/users', body)
  }

  editUser(email: string, body: { role?: string; name?: string; password?: string; directions?: string[] }) {
    return this.json<UserRow>(`/v1/users/${encodeURIComponent(email)}`, body, 'PUT')
  }

  async deleteUser(email: string): Promise<void> {
    await this.request<void>(`/v1/users/${encodeURIComponent(email)}`, { method: 'DELETE' })
  }

  // ---- connecting a Claude (OAuth consent, `features/oauth/Consent.tsx`) ----

  oauthRequest(id: string) {
    return this.request<OAuthRequest>(`/v1/oauth/requests/${encodeURIComponent(id)}`)
  }

  oauthAnswer(id: string, allow: boolean) {
    return this.json<{ redirect: string }>(`/v1/oauth/requests/${encodeURIComponent(id)}/${allow ? 'approve' : 'deny'}`, {})
  }

  // ---- devices & hubs ----

  async devices(filter: Partial<Record<'status' | 'point' | 'hub_id' | 'tiktok_account', string>> = {}) {
    const qs = new URLSearchParams(Object.entries(filter).filter(([, v]) => v) as [string, string][])
    const q = qs.toString()
    const { devices } = await this.request<{ devices: Device[] }>(`/v1/devices${q ? `?${q}` : ''}`)
    return devices
  }

  device(deviceId: string) {
    return this.request<Device>(`/v1/devices/${encodeURIComponent(deviceId)}`)
  }

  /** The fields that are ours on a phone: its name, which TikTok account lives on it, and a note. An empty
   *  string clears; the service answers 409 `account_in_use` when the label is already on another phone. */
  updateDevice(deviceId: string, body: { name?: string | null; tiktok_account?: string | null; note?: string | null; phone_number?: string | null }) {
    return this.json<Device>(`/v1/devices/${encodeURIComponent(deviceId)}`, body, 'PUT')
  }

  /** Forget a phone in our registry. The service refuses while it is live (it would re-register). */
  async deleteDevice(deviceId: string): Promise<void> {
    const res = await this.raw(`/v1/devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeJson(res))
  }

  /** Forget a machine and, with it, our phones and session claims for it. */
  deleteHub(hubId: string) {
    return this.request<{ hub_id: string; deleted_devices: string[] }>(
      `/v1/hubs/${encodeURIComponent(hubId)}`,
      { method: 'DELETE' },
    )
  }

  async hubs() {
    const { hubs } = await this.request<{ hubs: Hub[] }>('/v1/hubs')
    return hubs
  }

  // ---- my own keys, for my own agent over MCP ----
  //
  // Any signed-in person, for themselves only. The key is usable only through the MCP server, which
  // names the person to the API — so the phone an agent takes is theirs (ported from Device Lab).

  myKeys() {
    return this.request<{ keys: MyKey[]; limit: number }>('/v1/me/keys')
  }

  createMyKey() {
    return this.json<MyKey & { token: string; warning: string }>('/v1/me/keys', {})
  }

  revokeMyKey(keyId: string) {
    return this.json<MyKey>(`/v1/me/keys/${encodeURIComponent(keyId)}/revoke`, {})
  }

  deleteMyKey(keyId: string) {
    return this.request<{ deleted: boolean }>(`/v1/me/keys/${encodeURIComponent(keyId)}`, { method: 'DELETE' })
  }

  // ---- read keys for another service (the Keys page) ----
  //
  // Admin only. Unlike a personal key, presented straight to the API; read-only scopes only.

  serviceKeys() {
    return this.request<{ keys: ServiceKey[]; scopes: Record<string, string> }>('/v1/api-keys')
  }

  createServiceKey(name: string, scopes: string[]) {
    return this.json<ServiceKey & { token: string; warning: string }>('/v1/api-keys', { name, scopes })
  }

  revokeServiceKey(keyId: string) {
    return this.json<ServiceKey>(`/v1/api-keys/${encodeURIComponent(keyId)}/revoke`, {})
  }

  deleteServiceKey(keyId: string) {
    return this.request<{ deleted: boolean }>(`/v1/api-keys/${encodeURIComponent(keyId)}`, { method: 'DELETE' })
  }

  // ---- sessions ----

  async sessionStart(
    deviceId: string,
    opts: { tab?: string; here?: boolean } = {},
  ): Promise<SessionStart> {
    const path = `/v1/devices/${encodeURIComponent(deviceId)}/session:start`
    //: `tab` tells a reload (same tab) from a second tab; `here` is the deliberate "show it here".
    const res = await this.raw(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tab: opts.tab || undefined, here: opts.here || undefined }),
    })
    if (res.status === 409) {
      const body = (await safeJson(res)) as {
        detail?: { error?: string; held_for_s?: number; held_by?: string; posting?: string }
      }
      const info = body?.detail ?? {}
      // Only a *busy* or *posting* 409 means somebody is holding the phone. The service answers 409 for
      // "this phone cannot answer" too (unplugged, adb unauthorized), and treating every 409 as busy told
      // the tester someone was holding a phone that was not even connected.
      if (info.error === undefined || info.error === 'device_busy' || info.error === 'device_posting') {
        throw new DeviceBusyError(
          info.error === 'device_posting' ? 'device_posting' : 'device_busy',
          info.held_for_s ?? null, info.held_by ?? null, info.posting ?? null, body,
        )
      }
      throw new ApiError(409, body)
    }
    if (!res.ok) throw new ApiError(res.status, await safeJson(res))
    return (await res.json()) as SessionStart
  }

  async sessionStop(sessionId: string): Promise<void> {
    await this.raw(`/v1/sessions/${sessionId}/stop`, { method: 'POST' })
  }

  /** "Still here": renews the claim. Called from the idle warning's button — never on a timer. */
  async sessionKeep(sessionId: string): Promise<void> {
    const res = await this.raw(`/v1/sessions/${sessionId}/keep`, { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, await safeJson(res))
  }

  /** PNG screenshot of the live session (for saving). */
  screenshot(sessionId: string) {
    return this.raw(`/v1/sessions/${sessionId}/screen`)
  }

  // ---- jobs ----

  job(jobId: string) {
    return this.request<Job>(`/v1/jobs/${jobId}`)
  }

  jobLog(jobId: string, offset = 0) {
    return this.request<LogPage>(`/v1/jobs/${jobId}/log?offset=${offset}`)
  }

  // ---- videos ----

  async videos() {
    const { videos } = await this.request<{ videos: Video[] }>('/v1/videos')
    return videos
  }

  video(videoId: string) {
    return this.request<Video>(`/v1/videos/${videoId}`)
  }

  /** Announce a file; the bytes then go up in ranges to `upload_key` (see `upload.ts`). */
  createVideo(body: { filename: string; size: number; sha256: string; direction_id: string }) {
    return this.json<VideoCreated>('/v1/videos', body)
  }

  /** How much of a part the server already holds — where to resume from after a drop. */
  uploadOffset(key: string, uploadId: string) {
    return this.request<{ key: string; offset: number }>(
      `/v1/uploads/${key}?upload_id=${encodeURIComponent(uploadId)}`,
    )
  }

  /** One range of a resumable upload. `409 range_mismatch` carries the offset the server does hold. */
  async putRange(key: string, chunk: Blob, start: number, total: number, uploadId: string, signal?: AbortSignal) {
    const end = start + chunk.size - 1
    const res = await this.raw(`/v1/artifacts/${key}`, {
      method: 'PUT',
      body: chunk,
      signal,
      headers: {
        'Content-Type': 'application/octet-stream',
        'Content-Range': `bytes ${start}-${end}/${total}`,
        'X-Upload-Id': uploadId,
      },
    })
    if (res.status === 401) this.onUnauthorized?.()
    if (!res.ok) throw new ApiError(res.status, await safeJson(res))
    return (await res.json()) as RangeAccepted
  }

  /** Give a video a title; lists show it instead of the filename. An empty string clears it. */
  /** A video's direction — only while it has not been posted anywhere (409 `video_posted` after). */
  setVideoDirection(videoId: string, directionId: string) {
    return this.json<Video>(`/v1/videos/${encodeURIComponent(videoId)}`, { direction_id: directionId }, 'PUT')
  }

  renameVideo(videoId: string, title: string) {
    return this.json<Video>(`/v1/videos/${videoId}`, { title }, 'PUT')
  }

  async deleteVideo(videoId: string): Promise<void> {
    const res = await this.raw(`/v1/videos/${videoId}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeJson(res))
  }

  // ---- uniquification sources ----

  sources() {
    return this.request<SourcesList>('/v1/sources')
  }

  /** Announce a source (overlay clip or background image); bytes go up like a video's (`upload.ts`). */
  createSource(body: { kind: SourceKind; filename: string; size: number; sha256: string }) {
    return this.json<SourceCreated>('/v1/sources', body)
  }

  async deleteSource(sourceId: string): Promise<void> {
    const res = await this.raw(`/v1/sources/${sourceId}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeJson(res))
  }

  // ---- distributions ----

  distribute(videoId: string, targets: DistributeTarget[], uniquify: boolean, card: CardChoice | null = null, publish = true) {
    return this.json<DistributeStarted>(`/v1/videos/${videoId}/distribute`, {
      targets,
      uniquify,
      publish,
      ...(card ? { card } : {}),
    })
  }

  /** Stop a running distribution: queued renders are dropped, a render in flight is killed, nothing more is pushed. */
  cancelDistribution(distributionId: string) {
    return this.json<Distribution>(`/v1/distributions/${distributionId}/cancel`, {})
  }

  /** Remove a finished or stopped distribution from the list; the video and its variants stay. */
  async deleteDistribution(distributionId: string): Promise<void> {
    const res = await this.raw(`/v1/distributions/${distributionId}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeJson(res))
  }

  /** the push again, to one phone whose push failed — at most 3 times per phone (owner, 2026-10-15) */
  retryPush(distributionId: string, deviceId: string) {
    return this.json<{ job_id: string; device_id: string; try: number; of: number }>(
      `/v1/distributions/${distributionId}/devices/${deviceId}/push:retry`,
      {},
    )
  }

  retryPublish(distributionId: string, deviceId: string) {
    return this.json<{ job_id: string; device_id: string }>(
      `/v1/distributions/${distributionId}/devices/${deviceId}/publish:retry`,
      {},
    )
  }

  // ---- posting scenarios ----

  scenarios() {
    return this.request<ScenariosList>('/v1/scenarios')
  }

  createScenario(body: { name: string; steps: ScenarioStep[]; package?: string; recorded_on?: string }) {
    return this.json<Scenario>('/v1/scenarios', body)
  }

  /** `caption_step`: the index of the text step a post's caption is pasted into; null clears the mark. */
  editScenario(id: string, body: { name?: string; steps?: ScenarioStep[]; package?: string; caption_step?: number | null }) {
    return this.json<Scenario>(`/v1/scenarios/${id}`, body, 'PUT')
  }

  activateScenario(id: string) {
    return this.json<ScenariosList>(`/v1/scenarios/${id}/activate`, {})
  }

  deactivateScenario(id: string) {
    return this.json<ScenariosList>(`/v1/scenarios/${id}/deactivate`, {})
  }

  async deleteScenario(id: string): Promise<void> {
    const res = await this.raw(`/v1/scenarios/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeJson(res))
  }

  tryScenario(id: string, deviceId: string) {
    return this.json<{ job_id: string; status: string }>(`/v1/scenarios/${id}/try`, { device_id: deviceId })
  }

  recordStart(sessionId: string) {
    return this.json<{ recording: boolean; device_id: string }>(`/v1/sessions/${sessionId}/record:start`, {})
  }

  recordStop(sessionId: string) {
    return this.json<{ recording: boolean; device_id: string; steps: ScenarioStep[] }>(
      `/v1/sessions/${sessionId}/record:stop`,
      {},
    )
  }

  // ---- posts ----

  async posts() {
    const { posts } = await this.request<{ posts: Post[] }>('/v1/posts')
    return posts
  }

  post(id: string) {
    return this.request<Post>(`/v1/posts/${id}`)
  }

  createPost(body: PostEdit & { video_id: string }) {
    return this.json<Post>('/v1/posts', body)
  }

  editPost(id: string, body: PostEdit) {
    return this.json<Post>(`/v1/posts/${id}`, body, 'PUT')
  }

  async deletePost(id: string): Promise<void> {
    const res = await this.raw(`/v1/posts/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeJson(res))
  }

  checkPost(id: string) {
    return this.request<PostCheck>(`/v1/posts/${id}/check`)
  }

  /** `hold`: the variants render and wait for `releaseDistribution` — a look before anything is pushed */
  launchPost(id: string, hold = false) {
    return this.json<DistributeStarted & { post_id: string }>(`/v1/posts/${id}/launch${hold ? '?hold=true' : ''}`, {})
  }

  releaseDistribution(distributionId: string) {
    return this.json<{ distribution_id: string; released: boolean }>(`/v1/distributions/${distributionId}/release`, {})
  }

  /** One frame of the post as the render will hold it (banner, on-video text) — a PNG blob for an `<img>`. */
  async frame(postId: string, at: number, signal?: AbortSignal) {
    const res = await this.raw(`/v1/posts/${postId}/frame?at=${encodeURIComponent(String(at))}`, { signal })
    if (!res.ok) throw new ApiError(res.status, await safeJson(res))
    return res.blob()
  }

  async distributions(videoId?: string) {
    const q = videoId ? `?video_id=${encodeURIComponent(videoId)}` : ''
    const { distributions } = await this.request<{ distributions: Distribution[] }>(`/v1/distributions${q}`)
    return distributions
  }

  distribution(id: string) {
    return this.request<Distribution>(`/v1/distributions/${id}`)
  }
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    return null
  }
}
